import { ClientPanel } from "@/data/models/ClientPanel";
import { DeviceOverview } from "@/data/models/DeviceOverview";
import { isClientPanel } from "@/data/models/DeviceSettingsManager";

/** Why a device the user searched for is not offered in "Gerät zuordnen". */
export type HiddenDeviceHint =
  | { kind: "assignedToProject"; projectName: string }
  | { kind: "otherBranch"; branchName: string };

/**
 * Explains why "Gerät zuordnen" does not offer a device the user typed in exactly, so the dialog
 * never answers "Keine verfügbaren Geräte gefunden" for a device that exists (#2610143).
 *
 * Mirrors IRES Project's rule (Hans, 2026-10-31: "do it everywhere like PROJECT"): a device still in
 * a project names that project, otherwise a device of another Standort names its Standort. IRES Office
 * also matches the inventory number and the QR code, since its search does, and covers the Alle Geräte
 * devices too (Taimoor, 2026-11-02).
 *
 * @param query the search text; only an exact, case-insensitive match counts
 * @param hiddenDevices the client's non-deleted devices the dialog filtered out
 * @param matchesProjectBranch the dialog's own Standort filter, so a device hidden for another reason
 *   (in a project with no name recorded) is never blamed on its Standort
 * @param getBranchName resolves a branch number; no name means no Standort hint, as in IRES Project
 */
export function findHiddenDeviceHint(
  query: string,
  hiddenDevices: Array<ClientPanel | DeviceOverview>,
  matchesProjectBranch: (branchNumber: string | null | undefined) => boolean,
  getBranchName: (branchNumber: string) => string | undefined
): HiddenDeviceHint | null {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return null;
  }

  const device = hiddenDevices.find((candidate) =>
    [candidate.serialNumber, candidate.inventoryNumber, candidate.qrCode].some(
      (value) => !!value && value.trim().toLowerCase() === normalizedQuery
    )
  );
  if (!device) {
    return null;
  }

  const projectName = isClientPanel(device) ? device.currentProjectName : device.projectName;
  if (projectName) {
    return { kind: "assignedToProject", projectName };
  }

  if (matchesProjectBranch(device.branch)) {
    return null;
  }

  const branchName = device.branch ? getBranchName(device.branch) : undefined;
  return branchName ? { kind: "otherBranch", branchName } : null;
}
